import { REPORT, REPORT_RANGE } from '@consts/reports';
import { BaseReport } from '../BaseReport';
import { useReportLoad } from '@hooks/useReportLoad';
import { countUnique, sum } from '@utils/summaries';
import { basicSort, dateSort } from '@utils/sort';
import { useMemo } from 'react';
import { fixedDateRange, getUniqueDates, groupByKeys, lagDateRange, createRowsFromGroupedDates } from '@utils/data';
import { styleDateRange } from '@utils/styles';

interface NumberOfActiveLocationsProps {
    dateRange: { startDate: string; endDate: string };
}

export const NumberOfActiveLocations = ({ dateRange }: NumberOfActiveLocationsProps) => {
    const fixedDate = fixedDateRange(dateRange);

    const adjustedRange = useMemo(() => {
        return lagDateRange(fixedDate, 3);
    }, [dateRange]);

    const { data, hasData, loading } = useReportLoad({
        reportType: REPORT.RESELLER_NUMBER_OF_ACTIVE_LOCATIONS_BY_RESELLER,
        params: { ...adjustedRange, range: 'monthly' },
    });

    const groupedData = useMemo(() => {
        const groupedByReseller = groupByKeys(data, ['resellerName', 'date']);
        const dateKeys = getUniqueDates(data, REPORT_RANGE.MONTHLY);
        return createRowsFromGroupedDates(groupedByReseller, dateKeys, 3);
    }, [data]);

    return (
        <BaseReport
            title={'Number of Active Locations by Reseller'}
            description={
                'This table shows you the number of active locations each of your Resellers has by month, with a comparison to the last 3 prior months. Note that if you select more than 1 month in the date range above, each Reseller will have multiple rows (one for each full month).'
            }
            rangeText={styleDateRange([fixedDate.startDate, fixedDate.endDate])}
            loading={loading}
            hasData={hasData}
            tableOptions={{
                headers: [
                    'Reseller Name',
                    'Date',
                    'Locations selected Month',
                    'Locations 1 Months Prior',
                    'Locations 2 Months Prior',
                    'Locations 3 Months Prior',
                ],
                data: groupedData.map((items) => [
                    items[0]?.resellerName || 'Error: Could Not Find Reseller Name',
                    items[0]?.date || 'Error: Could Not Find Date',
                    items[1]?.locations || 0,
                    items[2]?.locations || 0,
                    items[3]?.locations || 0,
                    items[4]?.locations || 0,
                ]),
                summaries: [countUnique, countUnique, sum, sum, sum, sum],
                sort: [basicSort, dateSort(REPORT_RANGE.MONTHLY), basicSort, basicSort, basicSort, basicSort],
            }}
        />
    );
};
